import { Box, Text } from "@chakra-ui/react";
import { Instant, LocalDate, LocalDateTime } from "@js-joda/core";
import {
  entityProfileModalBorders,
  entityProfileModalColors,
} from "../../../../../entityProfileModal/entityProfileModal.theme";
import {
  EntityProfileSummaryActionRow as SummaryActionRow,
  EntityProfileSummaryBlock as SummaryBlock,
  EntityProfileSummaryRow as SummaryRow,
} from "../../../../../entityProfileModal/components/EntityProfileSummary";
import { dateFormatter } from "../../../../../../shared/utils/date-formatter";
import { phoneFormatter } from "../../../../../../shared/utils/phone-formatter";
import { PatientProfilePatient } from "../../../../patientProfileModal.types";
import EntityModalButton from "../../../../../entityProfileModal/components/EntityModalButton";

export type PatientProfileSummaryContactInformationProps = {
  isDeletingPhoneNumber: boolean;
  isEditing: boolean;
  patient: PatientProfilePatient;
  onAddPhoneNumber: () => void;
  onDeletePhoneNumber: (phoneNumber: PatientProfilePatient["phoneNumbers"][number]) => void;
};

function formatSummaryValue(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (value instanceof LocalDate) {
    return dateFormatter.toDate(value);
  }

  if (value instanceof LocalDateTime || value instanceof Instant) {
    return dateFormatter.toDateTime(value);
  }

  return String(value);
}

function formatPatientPhoneNumberLabel(phoneNumber: PatientProfilePatient["phoneNumbers"][number]) {
  switch (phoneNumber.type) {
    case "TEL":
      return "Home phone";
    case "MOBILE":
      return "Mobile phone";
    case "OTHER":
      return phoneNumber.label ?? "";
  }
}

export function PatientProfileSummaryContactInformation(
  props: PatientProfileSummaryContactInformationProps
) {
  const phoneNumbers = props.patient.phoneNumbers;

  return (
    <SummaryBlock title="Contact Information">
      <SummaryRow label="Email" value={formatSummaryValue(props.patient.email)} />
      {phoneNumbers.length === 0 ? (
        <Box borderBottom={entityProfileModalBorders.default} py={2}>
          <Text color={entityProfileModalColors.text} fontSize="14px">
            Patient has no phone numbers.
          </Text>
        </Box>
      ) : (
        phoneNumbers.map((phoneNumber) => (
          <SummaryRow
            key={phoneNumber.id}
            actions={
              props.isEditing ? (
                <EntityModalButton
                  isIconOnly
                  aria-label={`Delete ${formatPatientPhoneNumberLabel(phoneNumber)}`}
                  disabled={props.isDeletingPhoneNumber}
                  iconClassName="fa fa-trash"
                  kind="red"
                  size="xs"
                  title="Delete phone number"
                  variant="outline"
                  onClick={() => props.onDeletePhoneNumber(phoneNumber)}
                />
              ) : null
            }
            label={formatPatientPhoneNumberLabel(phoneNumber)}
            value={phoneFormatter.formatNationalIfValid(phoneNumber.phonenumber)}
          />
        ))
      )}
      {props.isEditing ? (
        <SummaryActionRow>
          <EntityModalButton
            iconClassName="fa fa-plus"
            kind="blue"
            size="sm"
            variant="outline"
            onClick={props.onAddPhoneNumber}
          >
            Add Phone
          </EntityModalButton>
        </SummaryActionRow>
      ) : null}
    </SummaryBlock>
  );
}
